import { unstyledRichTextComponents } from "@up/ui";

import type { RichTextComponents } from "@prismicio/react";

// Georgetown exposes a three-level heading scale as `.heading-1`/`.heading-2`/
// `.heading-3` (see styles/custom.scss), grouped with the element heading
// styles so applying them is visually lossless.
const DISPLAY_CLASS: Record<string, string> = {
  h1: "heading-1",
  h2: "heading-2",
  h3: "heading-3",
};

/**
 * Heading rich-text components that always apply a `.heading-N` class. By
 * default the class matches the block's tag (an `<h2>` → `<h2 class="heading-2">`).
 * When `display` (h1/h2/h3) is provided, every heading takes on that visual size
 * instead — decoupling a heading's semantic level from its appearance.
 */
export function headingComponents(display?: string | null): RichTextComponents {
  const override = display ? DISPLAY_CLASS[display] : undefined;

  return {
    heading1: ({ children, key }) => (
      <h1 className={override ?? "heading-1"} key={key}>
        {children}
      </h1>
    ),
    heading2: ({ children, key }) => (
      <h2 className={override ?? "heading-2"} key={key}>
        {children}
      </h2>
    ),
    heading3: ({ children, key }) => (
      <h3 className={override ?? "heading-3"} key={key}>
        {children}
      </h3>
    ),
    heading4: ({ children, key }) => (
      <h4 className={override ?? "heading-4"} key={key}>
        {children}
      </h4>
    ),
    heading5: ({ children, key }) => (
      <h5 className={override ?? "heading-5"} key={key}>
        {children}
      </h5>
    ),
    heading6: ({ children, key }) => (
      <h6 className={override ?? "heading-6"} key={key}>
        {children}
      </h6>
    ),
  };
}

/**
 * Georgetown's rich-text component set: the shared unstyled components plus the
 * `.heading-N` heading treatment. Pass a `display` level to override every
 * heading's visual size.
 */
export function georgetownRichTextComponents(
  display?: string | null,
): RichTextComponents {
  return {
    ...unstyledRichTextComponents(),
    ...headingComponents(display),
  };
}
